import { ASSET_URL, PROPERTY_SERVICE_URL, ENVIRONMENT } from '../env/Configurations';
import { ASSET_TYPE_PROPERTY } from '../constants/PropertyConst';
import {
  convertFileToBase64,
  calculateFileChecksum,
  calculateTotalChunks,
  CHUNK_SIZE,
  completeMultipartUpload,
  abortMultipartUpload,
  sanitizeFileName,
} from './commonUtils';
import { getPreSignedUrl } from './PropertyAPIUtils';
export const decodeMediaKeyValue = (mediaContent) => {
  const parseIfJson = (val) => {
    if (val == null) return null;
    let text = val;
    if (typeof text !== 'string') return text;
    try {
      if (
        /%[0-9A-Fa-f]{2}/.test(text) &&
        (text.includes('%7B') || text.includes('%5B') || text.includes('%22'))
      ) {
        text = decodeURIComponent(text);
      }
    } catch {}
    try {
      return JSON.parse(text);
    } catch {
      return null;
    }
  };

  if (!mediaContent) return { Video: null, GIF: null };
  if (Array.isArray(mediaContent)) {
    const get = (k) => {
      const row = mediaContent.find((x) => x?.key === k);
      return row ? parseIfJson(row.value) : null;
    };
    return { Video: get('Video'), GIF: get('GIF') };
  }

  return { Video: mediaContent.Video || null, GIF: mediaContent.GIF || null };
};

export const renderVideos = (videos) => {
  const files = videos?.files || videos?.clips || [];
  return files.map((video, idx) => ({
    id: video.id || video.videoId || `${video.fileName || 'video'}_${idx}`,
    url: video.mediaUrl || video.videoUrl || '',
    loopType: video.loopType === 'count' ? 'count' : 'always',
    loopCount: video.loopType === 'count' ? Number(video.loopCount) || 1 : 0,
    fileName: video.fileName || `video_${idx + 1}`,
    index: Number.isFinite(video.index) ? video.index : idx,
  }));
};

export const renderGifs = (gifs) => {
  const items = gifs?.files || gifs?.items || [];
  return items.map((gif, idx) => {
    const raw = gif.mediaUrl || gif.gifUrl || gif.url || '';
    const isHttp = /^https?:\/\//i.test(raw);
    const isBlob = /^blob:/i.test(raw);
    const previewUrl = isBlob ? '' : isHttp ? raw : raw ? `${ASSET_URL}${raw}` : '';
    return {
      id: gif.id || `${gif.fileName || 'gif'}_${idx}`,
      name: gif.fileName || `gif_${idx + 1}`,
      size: Number(gif.fileSize) || 0,
      file: null,
      url: previewUrl,
      gifUrl: isBlob ? '' : raw,
      index: Number.isFinite(gif.index) ? gif.index : idx,
    };
  });
};

export const uploadGifToCMS = async ({ file, groupId, propertyType, hotelId }) => {
  const fileSize = file.size;
  if (fileSize < 2 * 1024 * 1024) {
    const base64 = await convertFileToBase64(file);
    const input = {
      contentLength: file.size,
      contentType: 'image/gif',
      filename: sanitizeFileName(file.name),
      skipContentValidation: false,
      type: ASSET_TYPE_PROPERTY,
      contents: base64,
      groupId,
      propertyType,
    };
    const res = await getPreSignedUrl(input);
    const key = res?.data?.getPreSignUrlCMS?.key;
    if (!key) throw new Error('Failed to upload GIF');
    return key;
  }

  const totalChunks = calculateTotalChunks(fileSize);
  let uploadId = null;

  if (ENVIRONMENT === 'dev') {
    console.log(
      `Starting multipart GIF upload: ${file.name}, size: ${fileSize} bytes, chunks: ${totalChunks}`,
    );
  }

  try {
    const fileChecksum = await calculateFileChecksum(file);
    const startUploadResponse = await fetch(`${PROPERTY_SERVICE_URL}/startMultipartUpload`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        groupId,
        type: ASSET_TYPE_PROPERTY,
        contentType: 'image/gif',
        filename: sanitizeFileName(file.name),
        hotelId,
        propertyType,
        IsCMSRequest: true,
        fileSize,
        fileChecksum,
      }),
    });

    if (!startUploadResponse.ok) {
      throw new Error(`Failed to start GIF upload: ${startUploadResponse.status}`);
    }

    const chunkData = await startUploadResponse.json();
    uploadId = chunkData?.data?.key;

    if (!uploadId) {
      throw new Error('Failed to get upload ID from server');
    }

    if (ENVIRONMENT === 'dev') {
      console.log('GIF upload started, uploadId:', uploadId);
    }
    for (let chunkIndex = 0; chunkIndex < totalChunks; chunkIndex++) {
      const start = chunkIndex * CHUNK_SIZE;
      const end = Math.min(start + CHUNK_SIZE, fileSize);
      const fileChunk = file.slice(start, end);

      const base64Chunk = await convertFileToBase64(fileChunk);
      const response = await fetch(`${PROPERTY_SERVICE_URL}/uploadMultipartChunk`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          groupId,
          hotelId,
          uploadId,
          sequenceNumber: chunkIndex + 1,
          contents: base64Chunk,
        }),
      });

      if (!response.ok) {
        throw new Error(`Failed to upload GIF chunk ${chunkIndex + 1}: ${response.status}`);
      }

      await response.json();
      if (ENVIRONMENT === 'dev') {
        console.log(`GIF chunk ${chunkIndex + 1}/${totalChunks} uploaded`);
      }
    }
    if (ENVIRONMENT === 'dev') {
      console.log('All GIF chunks uploaded, completing multipart upload...');
    }

    const completeResponse = await completeMultipartUpload(groupId, hotelId, uploadId);

    if (ENVIRONMENT === 'dev') {
      console.log('GIF complete response:', JSON.stringify(completeResponse, null, 2));
    }

    if (completeResponse?.status === 'ok' && completeResponse?.data?.status === true) {
      const gifKey = completeResponse?.data?.message;
      if (!gifKey) {
        throw new Error('Failed to get GIF key from server');
      }
      if (ENVIRONMENT === 'dev') {
        console.log('GIF upload completed successfully, key:', gifKey);
      }
      return gifKey;
    } else {
      throw new Error('Failed to complete GIF upload');
    }
  } catch (error) {
    if (ENVIRONMENT === 'dev') {
      console.error('Error during GIF multipart upload:', error);
    }
    if (uploadId) {
      if (ENVIRONMENT === 'dev') {
        console.log('Aborting GIF multipart upload with uploadId:', uploadId);
      }
      try {
        await abortMultipartUpload(groupId, hotelId, uploadId);
        if (ENVIRONMENT === 'dev') {
          console.log('GIF multipart upload aborted successfully');
        }
      } catch (abortError) {
        if (ENVIRONMENT === 'dev') {
          console.error('Error aborting GIF multipart upload:', abortError);
        }
      }
    }
    throw error;
  }
};

export const toMediaContent = ({ videoList, videoImageUrl, gifList, gifImageUrl }) => {
  const videoFiles = (videoList || [])
    .filter((video) => (video.url || '').trim())
    .map((video, i) => ({
      fileName: video.fileName || `video_${i + 1}`,
      mediaUrl: video.url.trim(),
      loopType: video.loopType || 'always',
      loopCount: video.loopType === 'count' ? Number(video.loopCount) || 1 : 0,
      index: Number.isFinite(video.index) ? video.index : i,
    }));

  const gifFiles = (gifList || []).map((gif, i) => ({
    fileName: gif.name || `gif_${i + 1}`,
    mediaUrl: gif.gifUrl || gif.url || '',
    fileSize: Number.isFinite(Number(gif.size)) ? Number(gif.size) : Number(gif.fileSize) || 0,
    index: Number.isFinite(gif.index) ? gif.index : i,
  }));

  const hasVideo = videoFiles.length > 0 || (videoImageUrl && String(videoImageUrl).trim());
  const hasGif = gifFiles.length > 0 || (gifImageUrl && String(gifImageUrl).trim());

  if (!hasVideo && !hasGif) return [];
  const result = [];
  if (hasVideo) {
    result.push({
      key: 'Video',
      value: JSON.stringify({
        imageUrl: videoImageUrl || '',
        files: videoFiles,
      }),
    });
  }
  if (hasGif) {
    result.push({
      key: 'GIF',
      value: JSON.stringify({
        imageUrl: gifImageUrl || '',
        files: gifFiles,
      }),
    });
  }
  return result;
};

export const processGifsForUpload = async ({ rawGifs, groupId, propertyType, hotelId }) => {
  const gifData = [];
  for (let idx = 0; idx < rawGifs.length; idx++) {
    const gifObj = rawGifs[idx];
    const existingGif = (gifObj.gifUrl || gifObj.url || '').trim();
    const isBlob = /^blob:/i.test(existingGif);
    const isAlreadyUploaded = existingGif && !isBlob;
    let key = existingGif;
    if ((!key || isBlob) && gifObj.file) {
      key = await uploadGifToCMS({
        file: gifObj.file,
        groupId,
        propertyType,
        hotelId,
      });
    } else if (isAlreadyUploaded) {
      key = existingGif;
    }

    gifData.push({
      ...gifObj,
      gifUrl: key || '',
      url: key || gifObj.url || '',
      index: Number.isFinite(Number(gifObj.index)) ? Number(gifObj.index) : idx,
    });
  }
  return gifData;
};
